/** Controls above the «После лечения» image: treatment, then IOL type and toric version (D-028). */

import { FEATURES } from '../features';
import { correctionText, t, type StringKey } from '../i18n';
import { isAvailable } from '../optics/availability';
import { baseCorrection, isToric } from '../optics/profile';
import {
  IOL_TYPES, TREATMENT_GROUPS, composeCorrection, groupOf, groupProbe, hasToricVersion, iolTypeOf,
  type IolType, type TreatmentGroup,
} from '../optics/treatment';
import { state, update } from '../state';
import { h } from './dom';
import type { Controls } from './patient';

function availability() {
  return { age: state.age, cyl: state.cyl, keratoconus: state.keratoconus, aphakia: state.aphakia };
}

export function buildTreatmentControls(): Controls {
  const lang = state.lang;
  // Remembered sub-choices, so switching treatments back and forth keeps them.
  let iol: IolType = iolTypeOf(state.rightCorrection) ?? 'iol_tri';
  let toric = isToric(state.rightCorrection) || state.cyl !== 0;

  const choose = (group: TreatmentGroup): void => {
    update({ rightCorrection: composeCorrection(group, iol, toric && state.cyl !== 0) });
  };

  const groupButtons = new Map<TreatmentGroup, HTMLButtonElement>();
  const groupBox = h('div', { class: 'seg seg-wide', role: 'group' });
  for (const g of TREATMENT_GROUPS) {
    const btn = h('button', { type: 'button', class: 'seg-btn', 'data-group': g, onclick: () => choose(g) });
    groupButtons.set(g, btn);
    groupBox.append(btn);
  }

  const lensButtons = new Map<IolType, HTMLButtonElement>();
  const lensBox = h('div', { class: 'seg', role: 'group' });
  for (const type of IOL_TYPES) {
    const btn = h('button', {
      type: 'button', class: 'seg-btn', 'data-iol': type, text: t(lang, `lens.${type}` as StringKey),
      onclick: () => {
        iol = type;
        choose('iol');
      },
    });
    lensButtons.set(type, btn);
    lensBox.append(btn);
  }
  const lensRow = h('div', { class: 'sub' }, h('div', { class: 'group-title', text: t(lang, 'lens.title') }), lensBox);

  const plainBtn = h('button', {
    type: 'button', class: 'seg-btn', text: t(lang, 'version.plain'),
    onclick: () => {
      toric = false;
      choose(groupOf(state.rightCorrection));
    },
  });
  const toricBtn = h('button', {
    type: 'button', class: 'seg-btn', text: t(lang, 'version.toric'),
    onclick: () => {
      toric = true;
      choose(groupOf(state.rightCorrection));
    },
  });
  const versionRow = h('div', { class: 'sub' },
    h('div', { class: 'group-title', text: t(lang, 'version.title') }), h('div', { class: 'seg' }, plainBtn, toricBtn));

  const farBtn = h('button', { type: 'button', class: 'seg-btn', text: t(lang, 'target.far'), onclick: () => update({ monoTarget: 'far' }) });
  const nearBtn = h('button', { type: 'button', class: 'seg-btn', text: t(lang, 'target.near'), onclick: () => update({ monoTarget: 'near' }) });
  const targetRow = h('div', { class: 'sub' },
    h('div', { class: 'group-title', text: t(lang, 'target') }), h('div', { class: 'seg' }, farBtn, nearBtn));

  const desc = h('p', { class: 'treat-desc' });

  const el = h('section', { class: 'card ctrl ctrl-after' },
    h('h2', { text: t(lang, 'step.after') }),
    h('div', { class: 'group-title', text: t(lang, 'treat.title') }),
    groupBox,
    lensRow,
    versionRow,
    targetRow,
    desc,
  );

  function sync(): void {
    const s = state;
    const id = s.rightCorrection;
    const group = groupOf(id);
    const avail = availability();
    for (const [g, btn] of groupButtons) {
      const aphakicKey = `treat.${g}_aphakic` as StringKey;
      const hasAphakic = s.aphakia && (g === 'glasses' || g === 'rgp' || g === 'progressive');
      btn.textContent = t(s.lang, hasAphakic ? aphakicKey : (`treat.${g}` as StringKey));
      btn.hidden = !FEATURES.corrections.includes(groupProbe(g)) || !isAvailable(groupProbe(g), avail);
      btn.classList.toggle('on', g === group);
      btn.setAttribute('aria-pressed', g === group ? 'true' : 'false');
    }
    const currentIol = iolTypeOf(id);
    if (currentIol) iol = currentIol;
    lensRow.hidden = group !== 'iol';
    for (const [type, btn] of lensButtons) btn.classList.toggle('on', type === currentIol);
    versionRow.hidden = !(hasToricVersion(group) && s.cyl !== 0);
    plainBtn.classList.toggle('on', !isToric(id));
    toricBtn.classList.toggle('on', isToric(id));
    targetRow.hidden = baseCorrection(id) !== 'iol_mono';
    farBtn.classList.toggle('on', s.monoTarget === 'far');
    nearBtn.classList.toggle('on', s.monoTarget === 'near');
    desc.textContent = correctionText(s.lang, id, 'desc', { aphakia: s.aphakia });
  }

  sync();
  return { el, sync };
}
